import { useState, useEffect, useCallback, useRef } from "react";

export type NotificationPermission = "default" | "granted" | "denied";

interface UseBrowserNotificationsOptions {
  onPermissionChange?: (permission: NotificationPermission) => void;
}

// Extended notification options that include onclick handler
interface ExtendedNotificationOptions extends NotificationOptions {
  onclick?: (this: Notification, ev: Event) => void;
}

interface UseBrowserNotificationsResult {
  isSupported: boolean;
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  showNotification: (title: string, options?: ExtendedNotificationOptions) => Notification | null;
  isDocumentVisible: boolean;
}

export function useBrowserNotifications(
  options?: UseBrowserNotificationsOptions
): UseBrowserNotificationsResult {
  const [isSupported] = useState(() => "Notification" in window);
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    isSupported ? (Notification.permission as NotificationPermission) : "denied"
  );
  const [isDocumentVisible, setIsDocumentVisible] = useState(() =>
    typeof document !== "undefined" ? document.visibilityState === "visible" : true
  );
  const onPermissionChangeRef = useRef(options?.onPermissionChange);

  useEffect(() => {
    onPermissionChangeRef.current = options?.onPermissionChange;
  }, [options?.onPermissionChange]);

  // Track document visibility for showing notifications only when tab is not focused
  useEffect(() => {
    if (typeof document === "undefined") return;

    const handleVisibilityChange = () => {
      setIsDocumentVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!isSupported) {
      return "denied";
    }

    try {
      const result = await Notification.requestPermission();
      const newPermission = result as NotificationPermission;
      setPermission(newPermission);
      onPermissionChangeRef.current?.(newPermission);
      return newPermission;
    } catch {
      return "denied";
    }
  }, [isSupported]);

  const showNotification = useCallback(
    (title: string, options?: ExtendedNotificationOptions): Notification | null => {
      if (!isSupported || permission !== "granted") {
        return null;
      }

      // Don't show notification if document is visible (user is on the page)
      if (isDocumentVisible) {
        return null;
      }

      try {
        const notification = new Notification(title, {
          icon: "/favicon.ico",
          badge: "/favicon.ico",
          ...options,
        });

        // Auto-close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);

        // Focus window when notification is clicked
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          notification.close();
          // Call custom onClick if provided
          if (options?.onclick) {
            options.onclick.call(notification, event);
          }
        };

        return notification;
      } catch {
        return null;
      }
    },
    [isSupported, permission, isDocumentVisible]
  );

  return {
    isSupported,
    permission,
    requestPermission,
    showNotification,
    isDocumentVisible,
  };
}

// Helper function to check if notifications should be requested
export function shouldRequestNotificationPermission(): boolean {
  if (!("Notification" in window)) {
    return false;
  }
  return Notification.permission === "default";
}
